import React, { useState, useRef } from "react";
import EditIcon from "../../ui/EditIcon";
import {
  Form,
  Input,
  InputNumber,
  message,
  Table,
  Button,
  Typography,
} from "antd";
import { updateDietApi } from "../../../services/diet.services";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { getCookie } from "../../../helpers/localStorage";
import { Role } from "../../../constants/defaultKeys";
import { getTablePagination } from "../../../utils/tablePagination";

import ApproveRejectModal from "./ApproveRejectModal";
import dayjs from "dayjs";
import useIsMobile from "../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../hooks/usePageFillTableScroll";

const { Paragraph, Link } = Typography;

const renderExpandableText = (value) => {
  const text = typeof value === "string" ? value.trim() : value;
  if (!text) return "-";

  return (
    <Paragraph
      style={{ marginBottom: 0, wordBreak: "break-word" }}
      ellipsis={{
        rows: 2,
        expandable: "collapsible",
        symbol: (expanded) => (
          <Link style={{ fontSize: 12, marginLeft: 4 }}>
            {expanded ? "less" : "more"}
          </Link>
        ),
      }}
    >
      {text}
    </Paragraph>
  );
};

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode =
    inputType === "number" ? (
      <InputNumber className={inputStyles.mdNumberInput} step={1} min={0} />
    ) : (
      <Input className={inputStyles?.mdInput} style={{ width: "100%" }} />
    );
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: record?.key === "name" && true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};
const ServiceRequstTableEdit = ({
  loading,
  data,
  setData,
  page,
  setPage,
  refreshPage,
  userDetails,
  status,
}) => {
  const [form] = Form.useForm();
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(
    tableWrapRef,
    isMobile,
    "default",
    Array.isArray(data) ? data.length : data?.data?.length ?? 0
  );

  const [editingKey, setEditingKey] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const isEditing = (record) => record?.id === editingKey?.id;

  const edit = (record) => {
    form.setFieldsValue({
      name: "",
      protien: "",
      calories: "",
      ...record,
    });
    setEditingKey(record);
  };
  const cancel = () => {
    setEditingKey("");
  };
  const save = async (record) => {
    try {
      const updatedValues = await form.validateFields();
      const newData = [...data?.data];
      const index = newData.findIndex((item) => record.id === item.id);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, { ...item, ...updatedValues });
        setData(newData);
        const res = await updateDietApi(record?.id, updatedValues);
        refreshPage();
        message.success(res?.data?.message);
        setEditingKey("");
      } else {
        message.error("Row not found");
      }
    } catch (err) {
      message.error("Something went wrong");
      console.error(err?.response?.data?.message?.name[0]);
    }
  };
  const defaultRole = userDetails?.currRole || getCookie(Role);
  const formatRequestType = (value = "") =>
    value
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const columns = [
    {
      title: "Request Type",
      dataIndex: "request_type",
      width: isMobile ? 130 : 160,
      render: (value) => formatRequestType(value),
    },
    {
      title: "Patient Name",
      width: isMobile ? 140 : 180,
      ellipsis: true,
      render: (_, record) => record?.target_object_data?.patient_name || "-",
    },
    {
      title: "Request",
      width: isMobile ? 140 : 180,
      ellipsis: true,
      render: (_, record) => {
        const req = record?.target_object_data;
        return req?.service_name
          ? req?.service_name
          : req?.receipt_no
          ? `Receipt : ${req?.receipt_no}`
          : req?.file_name
          ? `File : ${req?.file_name}`
          : "-";
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      width: isMobile ? 200 : 280,
      render: (value) => renderExpandableText(value),
    },
    {
      title: "Requested By",
      dataIndex: "requested_by_name",
      width: isMobile ? 120 : 150,
      ellipsis: true,
    },
    {
      title: "Requested At",
      dataIndex: "created_at",
      width: isMobile ? 150 : 180,
      render: (value) =>
        value ? dayjs(value).format("DD MMM, YYYY hh:mm A") : "-",
    },
  ];

  if (status === "APPROVED") {
    columns.push(
      {
        title: "Approved By",
        dataIndex: "approved_by_name",
        width: isMobile ? 120 : 150,
        ellipsis: true,
      },
      {
        title: "Approved At",
        dataIndex: "approved_at",
        width: isMobile ? 150 : 180,
        render: (value) =>
          value ? dayjs(value).format("DD MMM, YYYY hh:mm A") : "-",
      }
    );
  } else if (status === "REJECTED") {
    columns.push(
      {
        title: "Rejected By",
        dataIndex: "approved_by_name",
        width: isMobile ? 120 : 150,
        ellipsis: true,
      },
      {
        title: "Rejected At",
        dataIndex: "approved_at",
        width: isMobile ? 150 : 180,
        render: (value) =>
          value ? dayjs(value).format("DD MMM, YYYY hh:mm A") : "-",
      },
      {
        title: "Admin Comment",
        dataIndex: "admin_comment",
        width: isMobile ? 200 : 280,
        render: (value) => renderExpandableText(value),
      }
    );
  } else {
    columns.push({
      title: "Action",
      dataIndex: "id",
      width: isMobile ? 90 : 100,
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => {
            setSelectedRecord(record);
            setModalOpen(true);
          }}
        >
          <EditIcon />
        </Button>
      ),
    });
  }

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === "name" ? "text" : "number",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });
  return (
    <>
      <Form form={form} component={false}>
        <div ref={tableWrapRef}>
          <Table
            className="no-vertical-borders"
            loading={loading}
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            bordered={false}
            dataSource={Array.isArray(data) ? data : data?.data}
            columns={mergedColumns}
            rowClassName="editable-row"
            tableLayout="fixed"
            pagination={getTablePagination({
              current: page,
              pageSize: 100,
              total: (Array.isArray(data) ? data.length : data?.total_count) ?? 0,
              onChange: (p) => setPage(p),
            })}
            scroll={tableScroll}
          />
        </div>
      </Form>
      <ApproveRejectModal
        open={modalOpen}
        setOpen={setModalOpen}
        record={selectedRecord}
        refresh={refreshPage}
      />
    </>
  );
};
export default ServiceRequstTableEdit;
